/**
 * useSyncQueue
 * -----------
 * Offline-first event synchronisation queue.
 *
 * • Persists pending operations to localStorage so they survive page reloads.
 * • Flushes immediately when the browser comes back online.
 * • Retries every RETRY_INTERVAL_MS seconds in the background.
 * • Stops after MAX_ATTEMPTS per item (keeps it in "failed" state so the UI
 *   can show a warning rather than looping forever).
 *
 * Usage
 * -----
 *   const { enqueue, pendingCount, failedCount, isSyncing, retryFailed } = useSyncQueue(flushFn);
 *
 *   flushFn(item) – async function that receives one queue item and must:
 *     • resolve on success  →  item is removed from the queue
 *     • throw on failure    →  item stays, attempt counter incremented
 *
 *   Queue item shape:
 *   {
 *     queueId: string,          // internal UUID
 *     operation: 'create' | 'update' | 'delete',
 *     payload: object,          // whatever the caller passes
 *     attempts: number,
 *     status: 'pending' | 'failed',
 *     createdAt: ISO string,
 *   }
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_KEY  = 'calendarSyncQueue';
const MAX_ATTEMPTS = 5;
const RETRY_INTERVAL_MS = 30_000; // 30 seconds

// ─── localStorage helpers ──────────────────────────────────────────────────────
const loadQueue = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const saveQueue = (q) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(q));
    } catch { /* quota exceeded – ignore */ }
};

// ──────────────────────────────────────────────────────────────────────────────
export const useSyncQueue = (flushFn) => {
    const [queue, setQueue]       = useState(() => loadQueue());
    const [isSyncing, setIsSyncing] = useState(false);
    const flushRef  = useRef(flushFn);
    const queueRef  = useRef(queue);

    // Keep refs current so closures always see latest values
    useEffect(() => { flushRef.current = flushFn; }, [flushFn]);
    useEffect(() => { queueRef.current = queue; saveQueue(queue); }, [queue]);

    // ── Core flush: process all pending items sequentially ────────────────────
    const flush = useCallback(async () => {
        const pending = queueRef.current.filter(i => i.status === 'pending');
        if (pending.length === 0 || !navigator.onLine) return;

        setIsSyncing(true);
        for (const item of pending) {
            try {
                await flushRef.current(item);
                // Success → remove from queue
                setQueue(prev => prev.filter(i => i.queueId !== item.queueId));
            } catch {
                // Failure → increment attempts or mark failed
                setQueue(prev => prev.map(i =>
                    i.queueId !== item.queueId ? i : {
                        ...i,
                        attempts: i.attempts + 1,
                        status: i.attempts + 1 >= MAX_ATTEMPTS ? 'failed' : 'pending',
                    }
                ));
            }
        }
        setIsSyncing(false);
    }, []);

    // ── Expose: add an item to the queue and kick off a flush attempt ─────────
    const enqueue = useCallback((operation, payload) => {
        const item = {
            queueId:   crypto.randomUUID(),
            operation,
            payload,
            attempts:  0,
            status:    'pending',
            createdAt: new Date().toISOString(),
        };
        setQueue(prev => [...prev, item]);
        // Non-blocking fire-and-forget
        setTimeout(() => flush(), 0);
        return item.queueId;
    }, [flush]);

    // ── Reset failed items back to pending so they'll be retried ─────────────
    const retryFailed = useCallback(() => {
        setQueue(prev => prev.map(i =>
            i.status === 'failed' ? { ...i, status: 'pending', attempts: 0 } : i
        ));
        setTimeout(() => flush(), 0);
    }, [flush]);

    // ── Flush on reconnection ─────────────────────────────────────────────────
    useEffect(() => {
        window.addEventListener('online', flush);
        return () => window.removeEventListener('online', flush);
    }, [flush]);

    // ── Periodic retry every RETRY_INTERVAL_MS ────────────────────────────────
    useEffect(() => {
        const timer = setInterval(flush, RETRY_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [flush]);

    // ── Flush once on mount (in case items survived a page reload) ────────────
    useEffect(() => {
        if (loadQueue().some(i => i.status === 'pending')) {
            setTimeout(() => flush(), 2000); // small delay to let the app initialise
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        enqueue,
        retryFailed,
        flush,
        isSyncing,
        pendingCount: queue.filter(i => i.status === 'pending').length,
        failedCount:  queue.filter(i => i.status === 'failed').length,
        queue,
    };
};

